'use client';

import { cn } from '@fshr/shared-react';
import {
	Input,
	Button,
	Select,
	Icon,
	ExpandableSection,
	IconChevronDown,
	IconChevronUp,
	HorizontalLine,
} from '../../components';
import { GuestDetail } from '../../types';
import React, { FC, PropsWithChildren } from 'react';
import { useState } from 'react';

type GuestDeatilProps = {
	t: (text: string, params?: (string | number)[]) => string;
	guestDetails: GuestDetail[] | undefined;
	registerField: any;
	numberOfAdults: number | undefined;
	numberOfChildren?: number;
	hideHeading?: boolean;
	hideReservationHolder?: boolean;
	expandable?: boolean;
	allowAddChildren?: boolean;
	excludeAdultLabel?: boolean;
	startingIndex?: number;
	roomId?: string;
	expandAllInputs?: boolean;
	expandFirstInput?: boolean;
};
interface InputProps {
	id: number;
	initialValue: string;
	display: boolean;
}

const NonExpandableAdultsForm: FC<{
	guest: any;
	expandable: boolean;
	registerField: any;
	t: (text: string, params?: (string | number)[] | undefined) => string;
	excludeAdultLabel?: boolean;
	startingIndex?: number;
	roomId?: string;
}> = ({
	guest,
	expandable,
	registerField,
	t,
	excludeAdultLabel = false,
	startingIndex = 2,
	roomId,
}) => {
	const inputPrefix = roomId ? `${roomId}.` : '';
	return (
		<React.Fragment>
			{!expandable ? (
				<h3 className="spacing-heading-xs text-subtitle2">
					<span className="text-text-primary">
						{t(`Guest ${guest.id + startingIndex}${excludeAdultLabel ? '' : ' (Adult)'}`)}
					</span>
				</h3>
			) : null}
			<div className="flex gap-100 w-full">
				<div className="w-full">
					<Input
						label={t('First Name')}
						placeholder={t('First Name')}
						{...registerField({
							name: `${inputPrefix}firstNameAdult${guest.id}`,
							initialValue: '',
							options: {
								required: false,
							},
						})}
					/>
				</div>
				<div className="w-full">
					<Input
						label={t('Last Name')}
						placeholder={t('Last Name')}
						{...registerField({
							name: `${inputPrefix}lastNameAdult${guest.id}`,
							initialValue: '',
							options: {
								required: false,
							},
						})}
					/>
				</div>
			</div>
		</React.Fragment>
	);
};

const GuestSectionWrapper: FC<
	PropsWithChildren<{
		sectionTitle?: string;
		isExpanded: boolean;
		onToggle: () => void;
	}>
> = ({ children, sectionTitle, isExpanded, onToggle }) => {
	return (
		<ExpandableSection
			additionalClasses={cn('flex flex-col w-full border-none spacing-stack-m')}
			elementsContainerClassName="py-[0px]"
			title={sectionTitle ?? ''}
			childrenClassName="flex flex-col spacing-card-stack-s"
			isExpanded={isExpanded}
			onToggle={onToggle}
			CustomExpandIcon={({ isExpanded }) =>
				isExpanded ? (
					<IconChevronUp width={16} height={16} />
				) : (
					<IconChevronDown className="max-w-[16px] max-h-[16px]" />
				)
			}
		>
			<div className="flex flex-col w-full justify-center">{children}</div>
		</ExpandableSection>
	);
};

const ExpandableAdultsForm: FC<{
	guest: any;
	index: number;
	expandable: boolean;
	registerField: any;
	onToggle: () => void;
	isExpanded: boolean;
	t: (text: string, params?: (string | number)[] | undefined) => string;
	excludeAdultLabel?: boolean;
	startingIndex?: number;
	roomId?: string;
}> = ({
	guest,
	index,
	expandable,
	registerField,
	onToggle,
	isExpanded,
	t,
	excludeAdultLabel = false,
	startingIndex = 2,
	roomId,
}) => {
	return (
		<GuestSectionWrapper
			sectionTitle={t(`Guest ${startingIndex + index}${excludeAdultLabel ? '' : ' (Adult)'}`)}
			isExpanded={isExpanded}
			onToggle={onToggle}
		>
			<NonExpandableAdultsForm
				expandable={expandable}
				registerField={registerField}
				t={t}
				guest={guest}
				roomId={roomId}
				startingIndex={startingIndex}
			/>
		</GuestSectionWrapper>
	);
};

const NonExpandableChildrenForm: FC<{
	input: any;
	index: number;
	expandable: boolean;
	numberOfAdults?: number;
	inputsLength: number;
	removeInput?: (id: number) => void;
	t: (text: string, params?: (string | number)[] | undefined) => string;
	registerField: any;
	birthYears: string[];
	allowAddChildren: boolean;
	roomId?: string;
}> = ({
	input,
	index,
	expandable,
	numberOfAdults,
	inputsLength,
	removeInput,
	t,
	registerField,
	birthYears,
	allowAddChildren,
	roomId,
}) => {
	const inputPrefix = roomId ? `${roomId}.` : '';
	return (
		<React.Fragment>
			{!expandable ? (
				<div className="flex items-center justify-between">
					<h3 className="spacing-heading-xs text-subtitle2">
						{t('Guest {0} (Child)', [numberOfAdults ? numberOfAdults + 1 + index : 0])}
					</h3>
					{allowAddChildren ? (
						<Button
							type="button"
							intent={'unstyled'}
							className="text-button1 cursor-pointer underline"
						>
							<p
								aria-disabled={inputsLength >= 5}
								onClick={() => {
									removeInput?.(input.id);
								}}
							>
								{t('Remove')}
							</p>
						</Button>
					) : null}
				</div>
			) : null}
			<div className="flex flex-col">
				<div className="flex gap-100 w-full">
					<div className="w-full">
						<Input
							label={t('First Name')}
							placeholder={t('First Name')}
							{...registerField({
								name: `${inputPrefix}firstName${input.id}`,
								initialValue: '',
								options: {
									required: false,
								},
							})}
						/>
					</div>
					<div className="w-full">
						<Select
							id="BirthYear"
							label={t('Birth Year')}
							placeholder={t('Birth Year')}
							className="text-body2 flex items-center w-full gap-100 h-[47px] bg-surface-form"
							{...registerField({
								name: `${inputPrefix}birthYear${input.id}`,
								initialValue: '',
								options: {
									required: false,
								},
							})}
							options={birthYears.map(year => {
								return {
									label: year,
									value: year,
								};
							})}
						/>
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

const ExpandableChildrenForm: FC<{
	input: any;
	index: number;
	isExpanded: boolean;
	onToggle: () => void;
	numberOfAdults?: number;
	t: (text: string, params?: (string | number)[] | undefined) => string;
	inputsLength: number;
	registerField: any;
	removeInput: (id: number) => void;
	expandable: boolean;
	birthYears: string[];
	allowAddChildren: boolean;
	roomId?: string;
}> = ({
	input,
	index,
	isExpanded,
	numberOfAdults,
	onToggle,
	t,
	inputsLength,
	registerField,
	birthYears,
	expandable,
	removeInput,
	allowAddChildren,
	roomId,
}) => {
	return (
		<GuestSectionWrapper
			sectionTitle={t('Guest {0} (Child)', [numberOfAdults ? numberOfAdults + 1 + index : 0])}
			isExpanded={isExpanded}
			onToggle={onToggle}
		>
			<NonExpandableChildrenForm
				birthYears={birthYears}
				inputsLength={inputsLength}
				numberOfAdults={numberOfAdults}
				registerField={registerField}
				removeInput={removeInput}
				t={t}
				expandable={expandable}
				input={input}
				index={index}
				allowAddChildren={allowAddChildren}
				roomId={roomId}
			/>
		</GuestSectionWrapper>
	);
};

export function GuestDetails(props: GuestDeatilProps) {
	const {
		t,
		guestDetails,
		registerField,
		numberOfAdults,
		numberOfChildren,
		hideHeading = false,
		hideReservationHolder = false,
		expandable = false,
		allowAddChildren = true,
		excludeAdultLabel = false,
		startingIndex,
		roomId,
		expandAllInputs = true,
		expandFirstInput = false,
	} = props;
	const actualYear = new Date().getFullYear();
	const birthYears = Array.from({ length: 18 }, (_, i) => (actualYear - i).toString());
	const [inputs, setInputs] = useState<InputProps[]>(
		numberOfChildren
			? Array.from({ length: numberOfChildren }, (_, index) => ({
					id: index,
					display: true,
					initialValue: '',
				}))
			: [1, 2, 3, 4, 5].map<InputProps>((v, index) => ({
					id: index,
					display: false,
					initialValue: '',
				}))
	);
	const reservationHolder = guestDetails?.find(guest => guest.isReservationHolder);
	const reservationHolderName = !reservationHolder
		? ''
		: `${reservationHolder.firstName} ${reservationHolder.lastName}`;

	const guestAdultsInputs = Array.from(
		{ length: !numberOfAdults ? 0 : numberOfAdults - (startingIndex ? startingIndex - 1 : 1) },
		(_, index) => ({
			id: index,
			display: false,
			initialValue: '',
		})
	);

	const [expandedAdultsState, setExpandedAdultsState] = useState<boolean[]>(
		Array(guestAdultsInputs.length).fill(
			expandAllInputs || expandFirstInput,
			expandAllInputs ? undefined : expandFirstInput ? 0 : undefined,
			expandAllInputs ? undefined : expandFirstInput ? 1 : undefined
		)
	);
	const [expandedChildrenState, setExpandedChildrenState] = useState<boolean[]>(
		Array(inputs.length).fill(expandAllInputs)
	);

	const addInput = () => {
		const hiddenInputIndex = inputs.findIndex(v => !v.display);
		if (hiddenInputIndex !== -1) {
			const updatedInputs = inputs.map((v, i) => {
				if (i === hiddenInputIndex) return { ...v, display: true };
				return v;
			});
			setInputs(updatedInputs);
		}
	};

	const removeInput = (id: number) => {
		const updatedInputs = inputs.filter(value => value.id !== id);
		updatedInputs.push({ id, display: false, initialValue: '' });
		setInputs(updatedInputs);
	};

	return (
		<div className="flex flex-col spacing-stack-l w-full">
			{!hideHeading && (
				<div className="flex flex-col">
					<h3 className="text-subtitle1 spacing-heading-s">{t('Guest Details')}</h3>
					<p className="text-body2">{t('Please provide all guest information.')}</p>
				</div>
			)}
			<div className="flex-col">
				<div className="flex flex-col spacing-stack-l">
					{!hideReservationHolder && (
						<div className="flex flex-col">
							<h3 className="spacing-heading-xs text-subtitle2">
								{t('Guest 1 (Reservation Holder)')}
							</h3>
							<dl className="flex flex-col">
								<dt className="text-text-secondary text-label">{t('Guest Name')}</dt>
								<dd className="text-body2">{reservationHolderName}</dd>
							</dl>
						</div>
					)}
					{guestAdultsInputs.map((guest, index) =>
						expandable ? (
							<>
								<HorizontalLine additionalClasses="border-border-subtle" />
								<ExpandableAdultsForm
									key={`adult_${index}`}
									guest={guest}
									index={index}
									t={t}
									excludeAdultLabel={excludeAdultLabel}
									expandable={expandable}
									registerField={registerField}
									isExpanded={expandedAdultsState[index]}
									onToggle={() => {
										setExpandedAdultsState(prev => {
											const newState = [...prev];
											newState[index] = !newState[index];
											return newState;
										});
									}}
									startingIndex={startingIndex}
									roomId={roomId}
								/>
							</>
						) : (
							<NonExpandableAdultsForm
								key={`adult_${index}`}
								guest={guest}
								expandable={expandable}
								registerField={registerField}
								t={t}
								excludeAdultLabel={excludeAdultLabel}
								startingIndex={startingIndex}
								roomId={roomId}
							/>
						)
					)}

					{inputs.map(
						(input, index) =>
							input.display &&
							(expandable ? (
								<>
									<HorizontalLine additionalClasses="border-border-subtle" />
									<ExpandableChildrenForm
										key={`child_${index}`}
										input={input}
										index={index}
										birthYears={birthYears}
										expandable={expandable}
										isExpanded={expandedChildrenState[index]}
										numberOfAdults={numberOfAdults}
										onToggle={() => {
											setExpandedChildrenState(prev => {
												const newState = [...prev];
												newState[index] = !newState[index];
												return newState;
											});
										}}
										registerField={registerField}
										removeInput={removeInput}
										t={t}
										inputsLength={0}
										allowAddChildren={allowAddChildren}
										roomId={roomId}
									/>
								</>
							) : (
								<NonExpandableChildrenForm
									key={`child_${index}`}
									input={input}
									index={index}
									expandable={expandable}
									inputsLength={inputs.length}
									t={t}
									removeInput={removeInput}
									registerField={registerField}
									birthYears={birthYears}
									allowAddChildren={allowAddChildren}
									numberOfAdults={numberOfAdults}
									roomId={roomId}
								/>
							))
					)}

					{allowAddChildren && inputs.some(input => !input.display) ? (
						<div className="flex gap-100 items-center">
							<Icon icon="plus_sign" />
							<Button onClick={addInput} intent="unstyled" className="flex text-button1 underline">
								{t('Add Child')}
							</Button>
						</div>
					) : null}
				</div>
			</div>
		</div>
	);
}
